import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createAppAsyncThunk } from "../../app/thunkWithTypes";
import AssetType from "../../api/models/fixProtocol/instrument/assetType";
import Security from "../../api/models/fixProtocol/instrument/security";
import { getAssetTypes, getSecurities } from "../../api/client";
import { RootState } from "../../app/store";
import { instanceToPlain, plainToInstance } from 'class-transformer';

interface SymbolSearchBarState {
  fetchAssetTypesStatus: 'idle' | 'pending' | 'succeeded' | 'failed'
  fetchSecuritiesStatus: 'idle' | 'pending' | 'succeeded' | 'failed'
  assetTypes: Array<Object>
  securities: Array<Object>
  securityChosen: Object | null
}

const intialState: SymbolSearchBarState = {
  fetchAssetTypesStatus: 'idle',
  fetchSecuritiesStatus: 'idle',
  assetTypes: [],
  securities: [],
  securityChosen: null,
}

export const fetchAssetTypes = createAppAsyncThunk('editPosition/fetchAssetTypes', async (data: { accessToken: string }) => {
  const { accessToken } = data;
  const assetTypes = await getAssetTypes(accessToken);
  return instanceToPlain(assetTypes);
}, {
  condition(arg, thunkApi) {
    const status = selectFetchAssetTypeStatus(thunkApi.getState())
    if (status !== 'idle') {
      return false;
    }
  }
})

export const fetchSecurities = createAppAsyncThunk('symbolSearchBar/fetchSecurities', async (data: { accessToken: string, symbolFilter: string, assetTypeIds: string[] }) => {
  const { accessToken, symbolFilter, assetTypeIds } = data;
  const securities: Security[] = [];
  await Promise.all(assetTypeIds.map(async assetTypeId => {
    securities.push(...(await getSecurities(accessToken, assetTypeId, symbolFilter)).filter(security => ["3", "4", "5", "6"].some(suffix => security.symbol.endsWith(suffix))));
  }))
  securities.sort((a, b) => a.symbol < b.symbol ? -1 : 1)
  return instanceToPlain(securities);
}, {
  condition(arg, thunkApi) {
    const status = selectFetchSecuritiesStatus(thunkApi.getState())
    if (status === 'pending') {
      return false;
    }
  }
})

const symbolSearchBarSlice = createSlice({
  name: 'symbolSearchBar',
  initialState: intialState,
  reducers: {
    choose: (state, action: PayloadAction<{ security: Object | null }>) => {
      state.securityChosen = action.payload.security;
      state.securities = [];
    },
    resetChosen: (state) => {
      state.securityChosen = null;
    },
    resetSecurities: (state) => {
      state.securities = [];
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAssetTypes.pending, (state, action) => {
        state.fetchAssetTypesStatus = 'pending'
      })
      .addCase(fetchAssetTypes.rejected, (state, action) => {
        state.fetchAssetTypesStatus = 'failed'
      })
      .addCase(fetchAssetTypes.fulfilled, (state, action) => {
        state.fetchAssetTypesStatus = 'succeeded'
        state.assetTypes = Object(action.payload)
      })
      .addCase(fetchSecurities.pending, (state, action) => {
        state.fetchSecuritiesStatus = 'pending'
      })
      .addCase(fetchSecurities.rejected, (state, action) => {
        state.fetchSecuritiesStatus = 'failed'
      })
      .addCase(fetchSecurities.fulfilled, (state, action) => {
        state.fetchSecuritiesStatus = 'succeeded'
        state.securities = Object(action.payload)
      })
  }
})

// Export selectors
export const selectFetchAssetTypeStatus = (state: RootState) => state.symbolSearch.fetchAssetTypesStatus;
export const selectFetchSecuritiesStatus = (state: RootState) => state.symbolSearch.fetchSecuritiesStatus;
export const selectSecurities = (state: RootState) => state.symbolSearch.securities;
export const selectSecurityChosen = (state: RootState) => state.symbolSearch.securityChosen;
export const selectAssetTypeIdsAcoes = createSelector([(state: RootState) => state.symbolSearch.assetTypes], (assetTypes) => {
  const assetTypeNames = ['ACAO PN', 'ACAO ON'];
  return plainToInstance(AssetType, assetTypes).filter(assetType => assetTypeNames.includes(assetType.name)).map(assetType => assetType.id ?? '');
});

// Export the auto-generated action creator with the same name
export const { choose, resetChosen, resetSecurities } = symbolSearchBarSlice.actions;

// Export the generated reducer function
export default symbolSearchBarSlice.reducer;